import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { TeacherMonthlyReport } from '@/types/salary';
import { toast } from 'sonner';

interface GenerateSlipResult {
  success: boolean;
  data?: {
    slipUrl?: string;
    tavi50Url?: string;
  };
  error?: string;
}

interface TeacherInfo {
  teacher_code: string;
  id_card_number?: string;
  address?: string;
  isTaxPnd3?: boolean;
}

export function useTeacherGenerateSlip() {
  const [isGenerating, setIsGenerating] = useState(false);

  const generateSlip = async (
    monthlyReport: TeacherMonthlyReport,
    teacherInfo: TeacherInfo,
    paidDate: string
  ): Promise<GenerateSlipResult> => {
    setIsGenerating(true);
    try {
      // Fetch teacher info from teacher_informations table (external schema)
      let citizenId = teacherInfo.id_card_number || '';
      let taxAddress = teacherInfo.address || '';

      try {
        const { data: teacherInfoData, error: infoError } = await (supabase as any)
          .schema('teacher')
          .from('teacher_informations')
          .select('uid, address')
          .eq('code', teacherInfo.teacher_code)
          .single();

        if (!infoError && teacherInfoData) {
          const info = teacherInfoData as { uid?: string; address?: string };
          citizenId = info.uid || citizenId;
          taxAddress = info.address || taxAddress;
        } else if (infoError) {
          console.warn('Could not fetch teacher_informations:', infoError);
        }
      } catch (fetchErr) {
        console.warn('Error fetching teacher_informations:', fetchErr);
      }

      const commissionAmount = monthlyReport.commission_amount || 0;
      const totalSalary = monthlyReport.total_salary || 0;
      const otherAmount = monthlyReport.other_amount || 0;
      const deductionAmount = monthlyReport.deduction_amount || 0;
      const depositRefund = monthlyReport.deposit_refund || 0;
      const depositDeduction = monthlyReport.deposit_deduction || 0;

      const subTotal = totalSalary + commissionAmount;
      const totalBeforeTax = subTotal + otherAmount - deductionAmount;
      const isTaxPnd3 = teacherInfo.isTaxPnd3 !== false;
      const taxAmount = isTaxPnd3 ? +(totalBeforeTax * 0.03).toFixed(2) : 0;
      const afterTaxTotal = isTaxPnd3 ? +(totalBeforeTax * 0.97).toFixed(2) : totalBeforeTax;
      const finalTotalWithDeposit = afterTaxTotal + depositRefund - depositDeduction;

      const { data, error } = await supabase.functions.invoke('generate-slip', {
        body: {
          data: {
            id: monthlyReport.id,
            type: "FS_TEACHER",
            code: teacherInfo.teacher_code,
            month: monthlyReport.month,
            employeeName: monthlyReport.teacher_name || '',
            baseSalary: totalSalary,
            totalSalary: totalBeforeTax,
            year: (monthlyReport.year + 543).toString(),
            payDate: paidDate,
            socialSecurity: "",
            compensationType: "COMMISSION",
            compensationAmount: commissionAmount,
            deductionType: "PND3",
            deductionAmount: taxAmount,
            lwopDeductionAmount: 0,
            totalDeduction: taxAmount,
            netSalary: finalTotalWithDeposit,
            loss: 0,
            taviData: {
              citizenId: citizenId,
              taxAddress: taxAddress,
            }
          }
        }
      });

      if (error) {
        console.error('Generate slip error:', error);
        toast.error('ไม่สามารถสร้างสลิปได้: ' + error.message);
        return { success: false, error: error.message };
      }

      toast.success('สร้างสลิปเรียบร้อยแล้ว');
      return { success: true, data: data?.data };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('Generate slip exception:', err);
      toast.error('เกิดข้อผิดพลาด: ' + message);
      return { success: false, error: message };
    } finally {
      setIsGenerating(false);
    }
  };

  return { generateSlip, isGenerating };
}
